/**
 * Turning a display name into something small enough for a tile.
 *
 * In `lib` rather than in one screen's model because three surfaces draw the
 * same tile — the sidebar's identity card, the profile's, and every row of an
 * organisation's roster — and two of them would otherwise be near-copies of
 * `collector.model.ts`. The rule the skill gives: a helper forked across two
 * features is a helper that will drift, and initials that disagreed would give
 * the same person two different tiles on one screen.
 */

/**
 * Two words give two letters; one gives one; three still give two.
 *
 * Split on whitespace only. A hyphenated surname is one name — "Michael
 * Hue-Williams" is MH, not MW — and splitting on the hyphen would take the
 * initial from the back half of a double-barrelled name.
 *
 * `fallback` is what a name with no words in it prints. `display_name` is
 * `not null` with a length check in the schema, so it should be impossible —
 * and a tile that silently rendered blank if it ever were is one nobody
 * notices is broken.
 */
export function initialsOf(name: string, fallback = "?"): string {
  const words = name.split(/\s+/).filter(Boolean);
  if (words.length === 0) return fallback;
  if (words.length === 1) return (words[0] ?? "").slice(0, 1).toUpperCase();

  return `${(words[0] ?? "").slice(0, 1)}${(words[words.length - 1] ?? "").slice(0, 1)}`.toUpperCase();
}

/**
 * The first word of a display name, or null for a name with no words in it.
 *
 * Only ever used to greet somebody. A display name is free text the account
 * holder chose, so this takes what they put first rather than trying to work
 * out which part is a given name.
 */
export function firstNameOf(name: string): string | null {
  const [first] = name.split(/\s+/).filter(Boolean);
  return first ?? null;
}
